/**
 * FFH-1743 arm coupon — the coupon a funnel arm asks checkout to apply when the
 * visitor brought none of their own.
 *
 * The forever-discount arm (/ai-team-rating-v3) sells the SAME Stripe prices as
 * the control funnel and differs only in the coupon redeemed: 50% off forever
 * instead of 50% off the first year. Which coupon that is comes from the Sanity
 * paywall block, so marketing can start and stop the arm without a deploy.
 *
 * It rides its own query param rather than reusing `?coupon=` because it must
 * NOT outrank a coupon the visitor arrived with — see resolveCheckoutCoupon.
 */

/** Query param `/checkout-stripe` reads the arm's coupon from. */
export const ARM_COUPON_QUERY_PARAM = 'armCoupon';

/**
 * Sentinel for "use the API's configured default coupon", stored by the Sanity
 * `checkoutCoupon` field so editors pick from a list rather than clearing a
 * value. Mirrors DEFAULT_CHECKOUT_COUPON_OPTION in
 * sanity/schemaTypes/paywallComponents/paywallBlock.ts — kept as a local
 * constant because app code doesn't import from the Studio config directory
 * (same reason as AI_TEAM_RATING_SETTINGS_ID in lib/sanity/queries.ts).
 */
export const DEFAULT_CHECKOUT_COUPON_OPTION = 'default';

/**
 * The coupon name a paywall should forward to checkout, or undefined to send
 * nothing and let the API apply DEFAULT_CHECKOUT_COUPON.
 *
 * Blank and the `default` sentinel both mean "no arm coupon": a paywall
 * authored before the field existed has no value at all, and one explicitly set
 * to Default has the sentinel. Both must behave identically to the control.
 */
export const resolveArmCoupon = (
  checkoutCoupon: string | undefined
): string | undefined => {
  const trimmed = checkoutCoupon?.trim();
  if (!trimmed || trimmed === DEFAULT_CHECKOUT_COUPON_OPTION) return undefined;
  return trimmed;
};

type CheckoutCouponSources = {
  /** `?coupon=` on the checkout URL itself. */
  queryCoupon: unknown;
  /** Coupon captured on funnel/join entry (useCaptureCouponFromUrl). */
  storedCoupon: string | null;
  /** `?armCoupon=` — the funnel arm's default, from Sanity. */
  armCoupon: unknown;
};

/**
 * Resolves which coupon reaches `/billing/checkout`.
 *
 * Precedence — **the visitor's own coupon always wins**:
 *
 *   1. `?coupon=` on this page's URL
 *   2. the captured coupon in session storage
 *   3. the arm coupon from the Sanity paywall
 *   4. nothing → the API applies DEFAULT_CHECKOUT_COUPON
 *
 * The arm coupon deliberately ranks LAST. Ranking it above the visitor's coupon
 * would let a 50%-off test arm override affiliate and creator links (several of
 * which are 100% off) and would defeat the `allow_coupon_entry` sentinel, whose
 * whole purpose is to let the visitor type their own code at Stripe. The arm
 * coupon is a default, not an override.
 *
 * Non-string values are ignored rather than coerced: a repeated query param
 * arrives as an array, and passing that on would send Stripe a nonsense name
 * that silently falls back to the default.
 */
export const resolveCheckoutCoupon = ({
  queryCoupon,
  storedCoupon,
  armCoupon
}: CheckoutCouponSources): string | undefined => {
  if (typeof queryCoupon === 'string' && queryCoupon) return queryCoupon;
  if (storedCoupon) return storedCoupon;
  if (typeof armCoupon === 'string' && armCoupon) return armCoupon;
  return undefined;
};
